import useAxios from '@/utilities/useAxios'

const BASE_URL = import.meta.env.VITE_DND_APP_BACKEND_URL

export interface ApiResponse<T> {
  httpStatus: string
  success: boolean
  message: string
  data: T
  errors: unknown | null
  errorCode: number
  timestamp: number
  path: string | null
  totalElements: number | null
  size: number
}

export interface DashboardQueryParams {
  userId: number | string
  roleName?: string | null
  sinceDate?: string
  untilDate?: string
  year?: number
}

export interface DashboardStatusBucket {
  count: number
  status_name: string
  description: string
}

export interface DashboardStatsData {
  pendingQuotation: DashboardStatusBucket
  completed: DashboardStatusBucket
  pendingSchedule: DashboardStatusBucket
  pendingApproval: DashboardStatusBucket
}

export interface DashboardStatsResponse {
  data: DashboardStatsData
}

export interface MonthlyCountItem {
  month: string
  value: number
}

interface MonthlyCountsApiItem {
  month: string
  count: number
}

type MonthlyCountsResponse = ApiResponse<MonthlyCountsApiItem[]>

export interface PieChartDataItem {
  type_of_concern: string
  count: number
  category: string
}

export interface PieChartParams extends DashboardQueryParams {
  category: string
  year?:number
}

export type ChartApiResponse = ApiResponse<PieChartDataItem[]>

export interface ChartDisplayData {
  label: string
  value: number
  category: string
  typeOfConcern: string
}

export interface PieChartHookState {
  data: ChartDisplayData[]
  rawData: PieChartDataItem[]
  loading: boolean
  error: string | null
  hasTimedOut: boolean
  refetch: () => Promise<void>
}

export interface PieChartHookOptions {
  enabled?: boolean
  refetchInterval?: number
  onSuccess?: (data: ChartDisplayData[]) => void
  onError?: (error: Error) => void
}

const buildDashboardQueryParams = ({
  userId,
  roleName,
  year,
  sinceDate,
  untilDate,
}: DashboardQueryParams): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    userId,
  }

  if (roleName) params.roleName = roleName
  if (year) params.year = year
  if (sinceDate) params.sinceDate = sinceDate
  if (untilDate) params.untilDate = untilDate

  return params
}

export const getDashboardStats = async (
  params: DashboardQueryParams,
  options?: { signal?: AbortSignal }
): Promise<DashboardStatsData> => {
  const url = `${BASE_URL}/v1/dashboard/stats`
  const { data } = await useAxios.get<DashboardStatsResponse>(url, {
    params: buildDashboardQueryParams(params),
    signal: options?.signal,
  })

  return data.data
}

export const getMonthlyTicketCounts = async (
  params: DashboardQueryParams,
  options?: { signal?: AbortSignal }
): Promise<MonthlyCountItem[]> => {
  const url = `${BASE_URL}/v1/dashboard/monthly-ticket-counts`

  const { data } = await useAxios.get<MonthlyCountsResponse>(url, {
    params: buildDashboardQueryParams(params),
    signal: options?.signal,
  })

  const items = data.data || []
  return items.map((item) => ({ month: item.month, value: item.count }))
}

export const fetchPieChartData = async (
  params: PieChartParams,
): Promise<ChartApiResponse> => {
  const hasUserId = params?.userId !== null && params?.userId !== undefined && String(params.userId).trim() !== ''
  if (!hasUserId) {
    throw new Error('fetchPieChartData called without userId')
  }

  if (!params?.category) {
    throw new Error('fetchPieChartData called without category')
  }

  const queryParams: Record<string, string | number> = {
    ...buildDashboardQueryParams(params),
    category: params.category,
  }

  const response = await useAxios.get<ChartApiResponse>(
    `${BASE_URL}/v1/dashboard/pie-chart-data`,
    { params: queryParams },
  )

  return response.data
}

export const chartApi = {
  getPieChartData: fetchPieChartData,
}
